/* =============================================================
   StudentDashboard.jsx — Student portal home screen
   -------------------------------------------------------------
   Shows the student's exchange status at a glance:
     - Destination university card
     - Exchange dates (static for demo)
     - Documents summary (real data from Supabase)
     - Process step tracker (real steps from process_steps table)
     - Upcoming deadlines (static for demo)

   Data sources:
     useStudentContext()        — resolves the active student (admin
                                  preview via ?as= or real student auth)
     useStudentProcess()        — current step index from DB
     useProcessConfig()         — ordered steps configured by the IO
     useStudentDocuments()      — uploaded documents from Supabase
   ============================================================= */

import { useState as useTabState } from 'react';
import { useNavigate } from 'react-router-dom';
import StudentLayout            from '../../components/StudentLayout';
import { useStudentContext }    from '../../hooks/useStudentContext';
import { useStudentProcess }   from '../../hooks/useStudentProcess';
import { useProcessConfig }    from '../../hooks/useProcessConfig';
import { useStudentTemplate }  from '../../hooks/useStudentTemplate';
import { usePartnerProcess }   from '../../hooks/usePartnerProcess';
import { useStudentDocuments } from '../../context/DocumentContext';
import { useTaskCompletions }  from '../../hooks/useTaskCompletions';
import { useAuth }             from '../../context/AuthContext';
import { buildDocList, getStudentCategory } from '../../lib/studentCategory';




/* ─── Status label helper ───────────────────────────────────── */
function docStatusLabel(status) {
  if (['uploaded', 'approved'].includes(status)) return { text: 'Approved',       cls: 'text-emerald-600' };
  if (status === 'task_done')                    return { text: 'Done',            cls: 'text-emerald-600' };
  if (status === 'task_pending')                 return { text: 'To confirm',      cls: 'text-amber-600'   };
  if (status === 'pending')                      return { text: 'Pending Review',  cls: 'text-amber-600'   };
  if (status === 'rejected')                     return { text: 'Changes needed',  cls: 'text-red-500'     };
  if (status === 'not_required')                 return { text: 'Not required',    cls: 'text-slate-400'   };
  return                                                { text: 'Missing',         cls: 'text-red-500'     };
}


/* ─── Horizontal stepper ────────────────────────────────────── */
function HorizontalStepper({ steps, currentStep, isFullyComplete = false }) {
  const n        = steps.length;
  const halfCell = 100 / (2 * n);
  const lineSpan = 100 - 2 * halfCell;
  const progFrac = isFullyComplete ? 1 : (currentStep <= 0 ? 0 : currentStep / (n - 1));
  const progWidth = progFrac * lineSpan;

  return (
    <div className="relative flex">
      {/* Background line */}
      <div className="absolute bg-slate-100 rounded-full"
        style={{ top: '16px', left: `${halfCell}%`, width: `${lineSpan}%`, height: '2px' }} />
      {/* Progress line with shimmer */}
      {progWidth > 0 && (
        <div className="absolute rounded-full transition-all duration-500 overflow-hidden"
          style={{ top: '16px', left: `${halfCell}%`, width: `${progWidth}%`, height: '2px', backgroundColor: '#4343d5' }}>
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.9) 50%, transparent 100%)',
            width: '40%',
            animation: 'progress-shimmer 2.8s ease-in-out infinite',
          }} />
        </div>
      )}
      <style>{`
        @keyframes progress-shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(350%); }
        }
      `}</style>
      {steps.map((step, i) => {
        const isDone    = isFullyComplete || i < currentStep;
        const isCurrent = !isFullyComplete && i === currentStep;
        return (
          <div key={step.id ?? i} className="flex-1 flex flex-col items-center">
            <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
              isDone    ? 'bg-indigo-600 border-indigo-600' :
              isCurrent ? 'bg-white border-indigo-600'      :
                          'bg-white border-slate-200'
            }`}>
              {isDone ? (
                <span className="material-symbols-outlined text-white text-[14px]">check</span>
              ) : isCurrent ? (
                <>
                  <span className="absolute inset-0 rounded-full bg-indigo-400 animate-ping opacity-30" />
                  <div className="w-3 h-3 bg-indigo-600 rounded-full" />
                </>
              ) : (
                <span className="text-[10px] font-bold text-slate-300">{i + 1}</span>
              )}
            </div>
            <div className="mt-2 text-center px-0.5 w-full">
              <p className={`text-[11px] font-semibold leading-tight ${
                isDone ? 'text-slate-600' : isCurrent ? 'text-indigo-700' : 'text-slate-300'
              }`}>{step.label}</p>
              {isCurrent && (
                <p className="text-[10px] mt-0.5 text-indigo-500 font-medium">● Current</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


/* ─── Skeleton loaders ──────────────────────────────────────── */
function CardSkeleton() {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 animate-pulse">
      <div className="w-10 h-10 rounded-xl bg-slate-100 mb-4" />
      <div className="h-5 bg-slate-100 rounded w-2/3 mb-2" />
      <div className="h-3 bg-slate-100 rounded w-1/2" />
    </div>
  );
}


/* ─── COMPONENT ─────────────────────────────────────────────── */
export default function StudentDashboard() {
  const navigate = useNavigate();

  /* ── Resolve active student ─────────────────────────────── */
  const { user } = useAuth();
  const { studentId, student, loading: studentLoading } = useStudentContext();

  /* ── Student direction (incoming / outgoing) ───────────── */
  const exchangeType = student?.type ?? 'outgoing';

  /* ── Resolve which process template applies to this student ── */
  const { templateId, loading: templateLoading } = useStudentTemplate(student);

  /* ── Process steps configured by the IO ────────────────── */
  const { steps, loading: stepsLoading } = useProcessConfig(templateId);

  /* ── Partner university process (when partner is on Unixplore) ── */
  const linkedUniId   = student?.partner_universities?.linked_university_id ?? null;
  const partnerUniName = student?.partner_universities?.name ?? null;
  const homeUniName   = student?.universities?.name ?? user?.university ?? 'Home';
  const { steps: partnerSteps, loading: partnerStepsLoading } = usePartnerProcess(linkedUniId);

  /* ── Active process tab ──────────────────────────────────── */
  const [activeProcessTab, setActiveProcessTab] = useTabState('own');

  /* ── Current step from Supabase ─────────────────────────── */
  const { currentStep, processLoading } = useStudentProcess(studentId);

  /* ── Documents uploaded by this student ─────────────────── */
  const { documents, docsLoading } = useStudentDocuments(studentId);

  /* ── Task completions ────────────────────────────────────── */
  const { completions: taskCompletions } = useTaskCompletions(studentId);

  // Mostra lo skeleton SOLO al primo caricamento assoluto (nessun dato ancora).
  // Se abbiamo già dati (anche parziali), li mostriamo subito e aggiorniamo
  // in background — evita che il token refresh di Supabase faccia sparire tutto.
  const isLoading = (studentLoading   && !student)
                 || (templateLoading  && !templateId)
                 || (stepsLoading     && steps.length === 0 && !!templateId)
                 || (processLoading   && !student)
                 || (docsLoading      && documents.length === 0 && !!studentId);

  /* ── Build required document list from process config ────── */
  // Uses buildDocList to handle the multi-doc-per-step structure and
  // filter docs by student category (exchange type + EU status).
  const studentCategory = getStudentCategory(student?.exchange_type, student?.eu_status);
  const allDocs = buildDocList(steps, documents, studentCategory, taskCompletions);

  const COMPLETE = ['uploaded', 'approved', 'task_done'];
  const INCOMPLETE = ['missing', 'rejected', 'task_pending'];

  const relevantDocs  = allDocs.filter(d => d.effectiveStatus !== 'not_required');
  const missingCount  = allDocs.filter(d => INCOMPLETE.includes(d.effectiveStatus)).length;
  const approvedCount = allDocs.filter(d => COMPLETE.includes(d.effectiveStatus)).length;

  // Fully complete: on the last step AND every doc/task in it is done
  const _lastStepDocs = allDocs.filter(d => d.stepIndex === steps.length - 1);
  const isFullyComplete = steps.length > 0
    && currentStep === steps.length - 1
    && _lastStepDocs.length > 0
    && _lastStepDocs.every(d => COMPLETE.includes(d.effectiveStatus));

  /* ── Destination label ───────────────────────────────────── */
  // Incoming students are visiting the admin's university (the host).
  // Outgoing students are going to their assigned partner/destination.
  const partnerName = exchangeType === 'incoming'
    ? (user?.university ?? '—')
    : (student?.partner_universities?.name ?? student?.destination_university ?? '—');

  const firstName = student?.name?.split(' ')[0] ?? 'there';

  /* ── Key dates ───────────────────────────────────────────── */
  // Primary: partner university's key_dates (if partner is on Unixplore and has set dates)
  // Fallback: home university's key_dates (always available)
  const partnerKeyDates = student?.partner_universities?.universities?.key_dates ?? [];
  const homeKeyDates    = student?.universities?.key_dates ?? [];
  const rawKeyDates     = partnerKeyDates.length > 0 ? partnerKeyDates : homeKeyDates;

  const COLOR_MAP = {
    red:     { bg: 'bg-red-100',     text: 'text-red-700',     dot: 'bg-red-400'     },
    amber:   { bg: 'bg-amber-100',   text: 'text-amber-700',   dot: 'bg-amber-400'   },
    blue:    { bg: 'bg-blue-100',    text: 'text-blue-700',    dot: 'bg-blue-400'    },
    indigo:  { bg: 'bg-indigo-100',  text: 'text-indigo-700',  dot: 'bg-indigo-400'  },
    slate:   { bg: 'bg-slate-100',   text: 'text-slate-600',   dot: 'bg-slate-400'   },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-400' },
    violet:  { bg: 'bg-violet-100',  text: 'text-violet-700',  dot: 'bg-violet-400'  },
    rose:    { bg: 'bg-rose-100',    text: 'text-rose-700',    dot: 'bg-rose-400'    },
  };

  /* ── Step progress URL: preserve the ?as= param ─────────── */
  // Navigation inside the student portal keeps the ?as= param so
  // the admin stays in preview mode across all pages.
  const studentSearch = studentId ? `?as=${studentId}` : '';


  /* ─── Loading state ─────────────────────────────────────── */
  if (isLoading) {
    return (
      <StudentLayout>
        <div className="mb-8">
          <div className="h-8 w-64 bg-slate-100 rounded-lg animate-pulse mb-2" />
          <div className="h-4 w-48 bg-slate-100 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-3 gap-6 mb-8">
          <CardSkeleton /><CardSkeleton /><CardSkeleton />
        </div>
      </StudentLayout>
    );
  }

  /* ─── No student found ──────────────────────────────────── */
  if (!student) {
    return (
      <StudentLayout>
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <span className="material-symbols-outlined text-slate-300 text-[64px] mb-4">person_off</span>
          <p className="text-lg font-semibold text-slate-600">Student not found</p>
          <p className="text-sm text-slate-400 mt-1">The student record could not be loaded.</p>
        </div>
      </StudentLayout>
    );
  }


  return (
    <StudentLayout>
      <div className="mb-8">
        <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">
          Welcome back, {firstName} 👋
        </h1>
        <p className="text-[#464555] mt-1">Here's your exchange status at a glance.</p>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-3 gap-6 mb-8">

        {/* Destination — clickable */}
        <button
          onClick={() => navigate(`/student/university${studentSearch}`)}
          className="bg-[#4343d5] rounded-2xl p-6 shadow-sm text-left hover:bg-indigo-700 transition-colors group"
        >
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center mb-4">
            <span className="material-symbols-outlined text-white text-[20px]">school</span>
          </div>
          <p className="font-display text-xl font-bold text-white line-clamp-1">{partnerName}</p>
          <p className="text-indigo-200 text-sm mt-1 flex items-center gap-1">
            Your exchange university
            <span className="material-symbols-outlined text-[14px] opacity-0 group-hover:opacity-100 transition-opacity">arrow_forward</span>
          </p>
        </button>

        {/* Exchange period — from partner_universities (outgoing) or universities (incoming) */}
        {(() => {
          const raw = exchangeType === 'incoming'
            ? student?.universities?.exchange_period
            : student?.partner_universities?.exchange_period;
          const parts  = raw ? raw.split(/\s*[-–]\s*/) : [];
          const start  = parts[0] ?? '—';
          const end    = parts[1] ?? null;
          return (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-amber-500 text-[20px]">calendar_month</span>
              </div>
              <p className="font-display text-2xl font-bold text-[#0b1c30]">{start}</p>
              <p className="text-[#464555] text-sm mt-1">Exchange start date</p>
              {end && <p className="text-xs text-slate-400 mt-0.5">Until {end}</p>}
            </div>
          );
        })()}

        {/* Documents — dynamic count, clickable */}
        <button
          onClick={() => navigate(`/student/documents${studentSearch}`)}
          className={`bg-white rounded-2xl p-6 shadow-sm border text-left transition-colors hover:bg-slate-50 group
            ${missingCount > 0 ? 'border-red-100' : 'border-slate-100'}`}
        >
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4
            ${missingCount > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}
          >
            <span className={`material-symbols-outlined text-[20px]
              ${missingCount > 0 ? 'text-red-500' : 'text-emerald-500'}`}
            >
              folder_open
            </span>
          </div>
          <p className="font-display text-2xl font-bold text-[#0b1c30]">
            {missingCount > 0 ? `${missingCount} missing` : 'All approved'}
          </p>
          <p className={`text-sm mt-1 ${missingCount > 0 ? 'text-red-500' : 'text-[#464555]'}`}>Documents required</p>
          <p className="text-xs text-indigo-600 font-medium mt-2 flex items-center gap-1 group-hover:gap-2 transition-all">
            View documents
            <span className="material-symbols-outlined text-[13px]">arrow_forward</span>
          </p>
        </button>
      </div>

      <div className="grid grid-cols-12 gap-6">

        {/* ── Exchange Progress — full width horizontal ── */}
        <div className="col-span-12 bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px] text-indigo-600">linear_scale</span>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Exchange Progress</h2>
            </div>
            <div className="flex items-center gap-3">
              {activeProcessTab === 'own' && steps.length > 0 && (
                <>
                  <span className="text-xs text-slate-400">
                    {isFullyComplete ? steps.length : currentStep} of {steps.length} phases completed
                  </span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    isFullyComplete ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-50 text-indigo-600'
                  }`}>
                    {isFullyComplete ? '100' : Math.round((currentStep / Math.max(steps.length, 1)) * 100)}%
                  </span>
                </>
              )}
              {/* Tabs — only when partner is on Unixplore */}
              {linkedUniId && (
                <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
                  <button
                    onClick={() => setActiveProcessTab('own')}
                    className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                      activeProcessTab === 'own' ? 'bg-white text-[#0b1c30] shadow-sm' : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {exchangeType === 'incoming' ? (homeUniName ?? 'Home') : (user?.university ?? 'Home')}
                  </button>
                  <button
                    onClick={() => setActiveProcessTab('partner')}
                    className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                      activeProcessTab === 'partner' ? 'bg-white text-[#0b1c30] shadow-sm' : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {partnerUniName ?? 'Partner'}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="px-8 py-6">
            {/* ── Own university process ── */}
            {activeProcessTab === 'own' && (
              steps.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No process steps configured yet.</p>
              ) : (
                <HorizontalStepper steps={steps} currentStep={currentStep} isFullyComplete={isFullyComplete} />
              )
            )}
            {/* ── Partner process ── */}
            {activeProcessTab === 'partner' && (
              partnerStepsLoading ? (
                <div className="flex gap-4 animate-pulse">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-slate-100" />
                      <div className="h-3 bg-slate-100 rounded w-16" />
                    </div>
                  ))}
                </div>
              ) : partnerSteps.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">
                  {partnerUniName} hasn't configured their process yet.
                </p>
              ) : (
                <HorizontalStepper steps={partnerSteps} currentStep={-1} />
              )
            )}
          </div>
        </div>

        {/* ── Key Dates — col-span-7 ── */}
        <div className="col-span-7 bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px] text-indigo-600">calendar_month</span>
            <h2 className="font-display text-base font-semibold text-[#0b1c30]">Key Dates</h2>
            <span className="ml-1 text-xs text-slate-400">
              — {partnerKeyDates.length > 0 ? partnerName : (student?.universities?.name ?? homeUniName)}
            </span>
            <button
              onClick={() => navigate(`/student/university${studentSearch}`)}
              className="ml-auto text-xs font-medium text-indigo-600 hover:underline flex items-center gap-1"
            >
              View university <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
            </button>
          </div>
          <div className="p-4 space-y-1">
            {rawKeyDates.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No key dates have been published yet.</p>
            ) : (
              rawKeyDates.map((d, i) => {
                const c = COLOR_MAP[d.color] ?? COLOR_MAP.indigo;
                return (
                  <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                    <div className={`flex-shrink-0 whitespace-nowrap text-xs font-bold px-2.5 py-1 rounded-lg ${c.bg} ${c.text}`}>
                      {d.date}
                    </div>
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`} />
                    <span className="text-sm text-[#464555]">{d.label}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ── My Documents — col-span-5 ── */}
        <div className="col-span-5 bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px] text-indigo-600">folder_open</span>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">My Documents</h2>
            </div>
            <button
              onClick={() => navigate(`/student/documents${studentSearch}`)}
              className="text-xs font-medium text-indigo-600 hover:underline flex items-center gap-1"
            >
              View all <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
            </button>
          </div>
          <div className="p-4 space-y-1">
            {relevantDocs.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No documents required yet.</p>
            ) : (
              relevantDocs.map((d, i) => {
                const { text, cls } = docStatusLabel(d.effectiveStatus);
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/student/documents${studentSearch}`)}
                  >
                    <span className="text-sm text-[#0b1c30]">{d.name}</span>
                    <span className={`text-xs font-semibold ${cls}`}>{text}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>

      </div>
    </StudentLayout>
  );
}
